import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export enum PointStatus {
  INACTIVE = 'inactive',
  ACTIVE = 'active',
  LATEST_ACTIVE = 'latest_active',
}

export type AddressPoint = {
  id: string;
  city: string;
  name: string;
  address: string;
  status: PointStatus;
  location?: [number, number];
  floor?: string;
  apartment?: string;
  accommodation?: string;
  building?: string;
  to_address_description?: string;
};

type AddressState = {
  map: boolean;
  editing_point: AddressPoint | null;
  points: AddressPoint[];

  openMap: () => void;
  closeMap: () => void;

  addPoint: (point: AddressPoint) => void;
  updatePoint: (point: AddressPoint) => void;
  openPoint: (point: AddressPoint) => void;
  activatePoint: (id: AddressPoint['id']) => void;
  removePoint: (id: AddressPoint['id']) => void;
};

export const useAddressStore = create<AddressState>()(
  persist(
    (set) => ({
      map: false,
      editing_point: null,
      points: [],

      openMap: () => set({ map: true }),
      closeMap: () => set({ map: false }),

      addPoint: (point) =>
        set((state) => {
          const prevActive = state.points.find(
            (p) => p.status === PointStatus.ACTIVE
          );

          const points = state.points
            .filter((item) => item.address !== point.address)
            .map((item) => {
              if (item.id === prevActive?.id) {
                return { ...item, status: PointStatus.LATEST_ACTIVE };
              }
              return { ...item, status: PointStatus.INACTIVE };
            });

          // New point should be the active one
          points.push({ ...point, status: PointStatus.ACTIVE });

          return {
            points,
            editing_point: null,
          };
        }),

      activatePoint: (id) =>
        set((state) => {
          const prevActive = state.points.find(
            (p) => p.status === PointStatus.ACTIVE
          );

          return {
            points: state.points.map((item) => {
              if (item.id === id)
                return { ...item, status: PointStatus.ACTIVE };
              if (item.id === prevActive?.id)
                return { ...item, status: PointStatus.LATEST_ACTIVE };
              return { ...item, status: PointStatus.INACTIVE };
            }),
            editing_point: null,
          };
        }),

      updatePoint: (point) =>
        set((state) => {
          const prevActive = state.points.find(
            (p) => p.status === PointStatus.ACTIVE
          );

          return {
            points: state.points.map((item) => {
              if (item.id === state.editing_point?.id) {
                return { ...point, status: PointStatus.ACTIVE };
              }
              if (item.id === prevActive?.id) {
                return { ...item, status: PointStatus.LATEST_ACTIVE };
              }
              return { ...item, status: PointStatus.INACTIVE };
            }),
            editing_point: null,
          };
        }),

      removePoint: (id) =>
        set((state) => ({
          points: state.points.filter((point) => point.id !== id),
        })),

      openPoint: (point) =>
        set({
          map: true,
          editing_point: point,
        }),
    }),
    {
      name: 'address-storage', // 🔐 storage key
      storage: createJSONStorage(() => localStorage),
    }
  )
);
